<template>
  <div class="player-game-report">
    <div class="report-header" @click="toggleReport" :class="{ collapsed: !isExpanded }">
      <div class="header-content">
        <div class="title-section">
          <h3>Full Game Assessment Report</h3>
          <div class="report-meta">
            <span class="match-info">{{ selectedGame.teamName }} vs
              {{ selectedGame.opposition }}</span>
            <span class="match-date">{{
              formatDateForHuman(selectedGame.matchDate)
            }}</span>
          </div>
        </div>
        <div class="toggle-icon">
          <i class="bi" :class="isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
      </div>
    </div>

    <transition name="slide-expand">
      <div class="full-player-report" v-show="isExpanded" :class="{ expanded: isExpanded }">
        <!-- Player Self Assessment Section -->
        <div class="assessment-section player-assessment">
          <div class="section-header">
            <h4>
              <i class="bi bi-person-fill"></i>
              Player Self Assessment
            </h4>
          </div>

          <div class="player-info">
            <div class="player-avatar">
              <img v-if="getPlayerImage(item.player.id)" class="avatar-image" :src="getPlayerImage(item.player.id)"
                :alt="`${item.player.name} Avatar`" />
              <img v-else class="avatar-image" :src="getImgUrl('default-image.png')" alt="Default Avatar" />
              <span class="player-name">{{ item.player.name }}</span>
            </div>
          </div>

          <div class="stats-grid">
            <div class="stat-item">
              <i class="bi bi-trophy-fill stat-icon"></i>
              <span class="stat-label">Goals</span>
              <span class="stat-value">{{ item.playerReport.goals || 0 }}</span>
            </div>
            <div class="stat-item">
              <i class="bi bi-hand-thumbs-up-fill stat-icon"></i>
              <span class="stat-label">Assists</span>
              <span class="stat-value">{{
                item.playerReport.assists || 0
              }}</span>
            </div>
            <div class="stat-item">
              <i class="bi bi-card-text stat-icon"></i>
              <span class="stat-label">Card</span>
              <span class="stat-value">{{
                item.playerReport.card || "None"
              }}</span>
            </div>
            <div class="stat-item">
              <i class="bi bi-clock-fill stat-icon"></i>
              <span class="stat-label">Time on Pitch</span>
              <span class="stat-value">{{ item.playerReport.session_mins || 0 }} mins</span>
            </div>
            <div v-if="item.playerReport.positionPlayed === 'Goalkeeper'" class="stat-item goalkeeper-stat">
              <i class="bi bi-shield-fill-check stat-icon"></i>
              <span class="stat-label">Clean Sheet</span>
              <span class="stat-value">{{
                item.playerReport.cleanSheet || "No"
              }}</span>
            </div>
          </div>

          <!-- Assessment Areas -->
          <div class="assessment-areas" :class="{ incomplete: !isPlayerReportComplete }">
            <div v-if="!isPlayerReportComplete" class="incomplete-warning">
              <i class="bi bi-exclamation-triangle-fill"></i>
              <span>Player Self Assessment not complete</span>
            </div>

            <div v-for="(area, key) in activeAssessmentAreas" :key="key" class="assessment-area">
              <h5 class="area-title">
                <i :class="getAreaIcon(area.title)"></i>
                {{ area.title }}
              </h5>

              <div class="area-content">
                <div class="feedback-item improvements">
                  <label class="feedback-label">
                    <i class="bi bi-arrow-up-circle"></i>
                    Areas for Improvement
                  </label>
                  <p class="feedback-text">
                    {{
                      item.playerReport[area.improvementsKey] ||
                      "No improvements noted"
                    }}
                  </p>
                </div>

                <div class="feedback-item positives">
                  <label class="feedback-label">
                    <i class="bi bi-check-circle"></i>
                    Positive Aspects
                  </label>
                  <p class="feedback-text">
                    {{
                      item.playerReport[area.positivesKey] ||
                      "No positives noted"
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Coach Assessment Section -->
        <div class="assessment-section coach-assessment">
          <div class="section-header">
            <h4>
              <i class="bi bi-clipboard-check-fill"></i>
              Coach Assessment
            </h4>
          </div>

          <div v-if="item.coachReport" class="coach-content">
            <div v-if="!item.isCoachReportComplete" class="incomplete-coach-warning">
              <i class="bi bi-exclamation-triangle-fill"></i>
              <span>Coach Assessment Incomplete</span>
            </div>

            <div class="coach-info">
              <div class="coach-avatar">
                <img v-if="getCoachImage(item.coachReport.coachId)" class="avatar-image"
                  :src="getCoachImage(item.coachReport.coachId)" alt="Coach Avatar" />
                <img v-else class="avatar-image" :src="getImgUrl('default-image.png')" alt="Coach Avatar" />
                <span class="coach-name">{{
                  getCoachName(item.coachReport.coachId)
                }}</span>
              </div>
            </div>

            <div class="coach-rating" v-if="
              userData.userType === 'Admin' || userData.userType === 'Coach'
            ">
              <div class="rating-display">
                <i class="bi bi-star-fill"></i>
                <span class="rating-label">Coach Rating:</span>
                <span class="rating-value">
                  {{
                    isStringNumberBetween0And5(item.coachReport.player_rating)
                      ? convertCoachRatingToWords(
                        item.coachReport.player_rating,
                      )
                      : item.coachReport.player_rating
                  }}
                </span>
              </div>
            </div>

            <div class="coach-feedback">
              <div class="feedback-item positives">
                <label class="feedback-label">
                  <i class="bi bi-check-circle"></i>
                  Positive Feedback
                </label>
                <p class="feedback-text coach-comments">
                  {{
                    item.coachReport.game_comments ||
                    "No positive feedback provided"
                  }}
                </p>
              </div>

              <div class="feedback-item improvements">
                <label class="feedback-label">
                  <i class="bi bi-arrow-up-circle"></i>
                  Areas for Improvement
                </label>
                <p class="feedback-text coach-comments">
                  {{
                    item.coachReport.game_improvements ||
                    "No improvement areas noted"
                  }}
                </p>
              </div>
            </div>

            <button v-if="
              (userData.userType === 'Admin' || userData.userType === 'Coach') && !$store.getters.isReadOnlySeason
            " @click="
              openCoachGamePerformanceModal(selectedGameReport || {}, 'Edit')
              " class="edit-assessment-btn">
              <i class="bi bi-pencil-square"></i>
              Edit Coach Assessment
            </button>
          </div>

          <div v-else class="missing-coach-assessment">
            <div class="missing-indicator">
              <i class="bi bi-exclamation-circle-fill"></i>
              <span>Coach Assessment Not Completed</span>
            </div>

            <div class="coach-placeholder">
              <img class="avatar-image" :src="getImgUrl('default-image.png')" alt="Coach Avatar" />
              <span class="placeholder-text">Awaiting coach feedback</span>
            </div>

            <button v-if="
              (userData.userType === 'Admin' || userData.userType === 'Coach') && !$store.getters.isReadOnlySeason
            " @click="
              openCoachGamePerformanceModal(selectedGameReport || {}, 'New')
              " class="add-assessment-btn">
              <i class="bi bi-plus-circle"></i>
              Add Coach Assessment
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { getTier } from "@/config/tenant";
import { avatarUrl } from "@/utils/avatar";

export default {
  name: "PlayerGameReport",
  props: {
    item: {
      type: Object,
      required: true,
    },
    selectedGame: {
      type: Object,
      required: true,
    },
    selectedGameReport: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    userData: {
      type: Object,
      required: true,
    },
    assessmentAreas: {
      type: Array,
      required: true,
    },
    isPlayerReportComplete: {
      type: Boolean,
      default: false,
    },
    getPlayersImage: {
      type: Function,
      required: false,
      default: () => null,
    },
    coachesData: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      isExpanded: false, // Start in closed state
    };
  },
  computed: {
    // Grassroots shows a single Positive + Improvement (stored in technically_*);
    // Pro keeps the full four assessment areas passed in via the prop.
    activeAssessmentAreas() {
      if (getTier() === "grassroots") {
        return [
          {
            title: "Self assessment",
            positivesKey: "technically_positives",
            improvementsKey: "technically_improvements",
          },
        ];
      }
      return this.assessmentAreas;
    },
  },
  methods: {
    toggleReport() {
      this.isExpanded = !this.isExpanded;
    },
    getImgUrl(pic) {
      // Empty stays empty (callers rely on rendering nothing); a stored
      // "default-image.png" resolves to the neutral avatar via avatarUrl.
      return pic !== undefined && pic !== "" ? avatarUrl(pic) : "";
    },
    getPlayerImage(playerId) {
      // Use the prop function instead of accessing parent
      if (this.getPlayersImage && typeof this.getPlayersImage === "function") {
        try {
          const imagePath = this.getPlayersImage(playerId);
          if (!imagePath) return null;
          // If already a full URL or has /images/ prefix, use as-is
          if (imagePath.startsWith('http') || imagePath.startsWith('/images/')) {
            return imagePath;
          }
          // Add /images/ prefix for production compatibility
          return `/images/${imagePath}`;
        } catch (error) {
          console.warn("Error getting player image:", error);
          return null;
        }
      }
      return null;
    },
    formatDateForHuman(date) {
      if (!date) return "Invalid date";
      const d = new Date(date);
      if (isNaN(d.getTime())) return "Invalid date";
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const monthName = months[d.getMonth()];
      const day = d.getDate();
      const year = d.getFullYear();
      // Ordinal suffix
      function ordinal(n) {
        const s = ["th", "st", "nd", "rd"],
          v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      }
      return `${monthName} ${ordinal(day)} ${year}`;
    },
    isStringNumberBetween0And5(str) {
      const num = Number(str);
      return !isNaN(num) && num >= 0 && num <= 5;
    },
    convertCoachRatingToWords(rating) {
      const ratingMap = {
        0: "No Rating",
        1: "Unsatisfactory",
        2: "⁠⁠Requires improvement",
        3: "Progressing well",
        4: "Good",
        5: "Excellent",
      };
      return ratingMap[rating] || rating;
    },
    openCoachGamePerformanceModal(report, mode) {
      // Emit event to parent instead of direct access
      // Include the item (player data) so parent knows which player this is for
      this.$emit("open-coach-modal", { report, mode, player: this.item.player, playerReport: this.item.playerReport });
    },
    getAreaIcon(areaTitle) {
      const icons = {
        Technical: "bi bi-gear-fill",
        Tactical: "bi bi-diagram-3-fill",
        Physical: "bi bi-heart-pulse-fill",
        Mental: "bi bi-brain-fill",
      };
      return icons[areaTitle] || "bi bi-circle-fill";
    },
    getCoachName(coach_id) {
      return (
        this.coachesData.find((coach) => coach.id === coach_id)?.name || "Coach"
      );
    },
    getCoachImage(coach_id) {
      return this.getImgUrl(
        this.coachesData.find((coach) => coach.id === coach_id)?.userImage ||
        "default-image.png",
      );
    },
  },
};
</script>

<style scoped lang="scss">
.player-game-report {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  margin: 1.5rem 0;
  border: 1px solid #e9ecef;
  width: 100%;
}

.report-header {
  background: linear-gradient(135deg, #b5d4f5, #718eaa);
  color: white;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    background: linear-gradient(135deg, #cfd8d7, #69798a);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
  }

  &.collapsed {
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
  }

  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .title-section {
    flex-grow: 1;
    text-align: left;

    h3 {
      margin: 0 0 0.5rem 0;
      font-size: 1.5rem;
      font-weight: 700;
    }

    .report-meta {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;
      opacity: 0.9;

      .match-info {
        font-weight: 600;
        font-size: 1.1rem;
      }

      .match-date {
        font-size: 0.95rem;
      }
    }
  }

  .toggle-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    transition: all 0.3s ease;

    i {
      font-size: 1.2rem;
      transition: transform 0.3s ease;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: scale(1.1);
    }
  }
}

.full-player-report {
  padding: 0;
}

.assessment-section {
  padding: 2rem;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &.player-assessment {
    background: linear-gradient(135deg, #f8f9ff, #ffffff);
  }

  &.coach-assessment {
    background: linear-gradient(135deg, #fff8f0, #ffffff);
  }
}

.section-header {
  margin-bottom: 1.5rem;

  h4 {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 0 0 0.5rem 0;
    font-size: 1.3rem;
    font-weight: 700;
    color: #2c3e50;

    i {
      font-size: 1.4rem;
      color: #007bff;
    }
  }

  .creation-date {
    margin: 0;
    font-size: 0.9rem;
    color: #6c757d;
    font-style: italic;
  }
}

.player-info,
.coach-info {
  margin-bottom: 1.5rem;
}

.player-avatar,
.coach-avatar {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  border: 1px solid #e9ecef;

  .avatar-image {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid #007bff;
  }

  .player-name,
  .coach-name {
    font-weight: 700;
    font-size: 1.1rem;
    color: #2c3e50;
  }
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;

  .stat-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    background: white;
    border-radius: 12px;
    border: 1px solid #e9ecef;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    &.goalkeeper-stat {
      border-color: #28a745;
      background: linear-gradient(135deg, #d4edda, #c3e6cb);
    }

    .stat-icon {
      font-size: 1.5rem;
      color: #007bff;
      min-width: 24px;
    }

    .stat-label {
      font-weight: 600;
      color: #495057;
      flex-grow: 1;
    }

    .stat-value {
      font-weight: 700;
      color: #2c3e50;
      font-size: 1.1rem;
    }
  }
}

.assessment-areas {
  &.incomplete {
    opacity: 0.7;
    border: 2px dashed #ffc107;
    border-radius: 12px;
    padding: 1rem;
    background: #fff3cd;
  }
}

.incomplete-warning {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #856404;
  font-weight: 600;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(255, 193, 7, 0.1);
  border-radius: 8px;

  i {
    font-size: 1.2rem;
  }
}

.assessment-area {
  margin-bottom: 2rem;
  background: white;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;

  .area-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 0;
    padding: 1rem 1.5rem;
    font-size: 1.1rem;
    font-weight: 700;
    color: white;
    background: linear-gradient(135deg, #495057, #6c757d);

    i {
      font-size: 1.3rem;
    }
  }

  .area-content {
    padding: 1.5rem;
  }
}

.feedback-item {
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }

  .feedback-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 700;
    color: #495057;
    margin-bottom: 0.5rem;
    font-size: 0.95rem;

    i {
      font-size: 1rem;
    }
  }

  .feedback-text {
    margin: 0;
    padding: 1rem;
    border-radius: 8px;
    line-height: 1.6;
    color: #2c3e50;
    min-height: 3rem;
    display: flex;
    align-items: center;
  }

  &.improvements .feedback-text {
    background: linear-gradient(135deg, #fff3cd, #ffeaa7);
    border-left: 4px solid #ffc107;
  }

  &.positives .feedback-text {
    background: linear-gradient(135deg, #d4edda, #c3e6cb);
    border-left: 4px solid #28a745;
  }
}

.coach-content {
  .coach-rating {
    margin-bottom: 1.5rem;

    .rating-display {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 1rem;
      background: linear-gradient(135deg, #fff3cd, #ffeaa7);
      border-radius: 12px;
      border: 1px solid #ffc107;

      i {
        font-size: 1.5rem;
        color: #ffc107;
      }

      .rating-label {
        font-weight: 600;
        color: #495057;
      }

      .rating-value {
        font-weight: 700;
        color: #2c3e50;
        font-size: 1.1rem;
      }
    }
  }
}

.missing-coach-assessment {
  text-align: center;
  padding: 2rem;

  .missing-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    color: #dc3545;
    font-weight: 700;
    font-size: 1.1rem;
    margin-bottom: 1.5rem;

    i {
      font-size: 1.5rem;
    }
  }

  .coach-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2rem;
    opacity: 0.6;

    .avatar-image {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      filter: grayscale(100%);
    }

    .placeholder-text {
      font-style: italic;
      color: #6c757d;
    }
  }

  .add-assessment-btn {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 2rem;
    background: linear-gradient(135deg, #28a745, #20c997);
    color: white;
    border: none;
    border-radius: 12px;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    margin: 0 auto;

    &:hover {
      background: linear-gradient(135deg, #20c997, #17a2b8);
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(40, 167, 69, 0.3);
    }

    i {
      font-size: 1.2rem;
    }
  }
}

.incomplete-coach-warning {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #856404;
  font-weight: 600;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 193, 7, 0.15);
  border-radius: 8px;
  border: 1px solid rgba(255, 193, 7, 0.3);

  i {
    font-size: 1.1rem;
  }
}

.edit-assessment-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;

  &:hover {
    background: linear-gradient(135deg, #0056b3, #004085);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
  }

  i {
    font-size: 1rem;
  }
}

// Responsive Design
@media (max-width: 768px) {
  .assessment-section {
    padding: 1.5rem;
  }

  .stats-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .report-header {
    padding: 1rem;

    .report-meta {
      flex-direction: column;
      gap: 0.5rem;
    }
  }

  .player-avatar,
  .coach-avatar {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
  }

  .feedback-item .feedback-label {
    font-size: 0.9rem;
  }
}

// Animation
@keyframes slideInFromBottom {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.player-game-report {
  animation: slideInFromBottom 0.6s ease forwards;
}

// Slide expand/collapse animation
.slide-expand-enter-active,
.slide-expand-leave-active {
  transition: all 0.4s ease;
  overflow: hidden;
}

.slide-expand-enter-from,
.slide-expand-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

.slide-expand-enter-to,
.slide-expand-leave-from {
  max-height: 2000px; // Large enough to accommodate content
  opacity: 1;
  transform: translateY(0);
}

.full-player-report {
  transition: all 0.3s ease;

  &.expanded {
    animation: expandContent 0.4s ease forwards;
  }
}

@keyframes expandContent {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
